import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import { Mail, CheckCircle, Sparkles } from "lucide-react";
import HelpGenieLogo from "@/components/ui/HelpGenieLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getCanonicalUrl } from "@/config/site";
import { useAcceptGenieInvite } from "@/hooks/agent/useAgentPage";
import { GenieBranding } from "@/types/ui/branding";

const DEFAULT_PRIMARY = "#4E9CFF";
const DEFAULT_SECONDARY = "#FF4DB8";

interface Props {
  urlName: string;
  genieName: string;
  agentId: string;
  branding?: GenieBranding | null;
  userEmail?: string | null;
  onSkip: () => void;
  onSuccess: (consumerId: string | null) => void;
}

interface FormValues {
  email: string;
}

const GenieInvitePage = ({ urlName, genieName, agentId, branding, userEmail, onSkip, onSuccess }: Props) => {
  const [submitted, setSubmitted] = useState(false);
  const { mutate: acceptInvite, isPending } = useAcceptGenieInvite();
  const { register, handleSubmit, formState: { errors } } = useForm<FormValues>();

  useEffect(() => {
    if (!userEmail) return;
    acceptInvite({ agentId }, { onSuccess: (data) => onSuccess(data.consumerId) });
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const primary = branding?.primaryColor || DEFAULT_PRIMARY;
  const secondary = branding?.secondaryColor || DEFAULT_SECONDARY;
  const logoUrl = branding?.logoUrl || null;
  const bgColor = branding?.backgroundColor || null;

  const gradient = `linear-gradient(135deg, ${primary}, ${secondary})`;
  const pageBg = bgColor
    ? bgColor
    : `linear-gradient(135deg, ${primary}08, white 50%, ${secondary}08)`;

  const onSubmit = ({ email }: FormValues) => {
    acceptInvite(
      { agentId, urlName, email },
      {
        onSuccess: (data) => {
          setSubmitted(true);
          setTimeout(() => onSuccess(data.consumerId), 1500);
        },
      },
    );
  };

  return (
    <>
      <Helmet>
        <title>You're Invited | Help Genie</title>
        <meta name="robots" content="noindex,nofollow" />
        <link rel="canonical" href={getCanonicalUrl("/invite")} />
      </Helmet>

      <div
        className="min-h-screen flex flex-col items-start justify-start pt-16 px-4 pb-12"
        style={{ background: pageBg }}
      >
        <div className="w-full flex flex-col items-center">
          <div className="flex justify-center mb-6 p-2">
            {logoUrl ? (
              <img src={logoUrl} alt={genieName} className="h-14 w-14 rounded-2xl object-cover shadow-sm" />
            ) : (
              <HelpGenieLogo size={56} />
            )}
          </div>

          <div
            className="w-full max-w-lg bg-white rounded-2xl shadow-lg overflow-hidden"
            style={{ border: `1px solid ${primary}25` }}
          >
            <div style={{ height: 4, background: gradient }} />

            <div className="p-8 flex flex-col items-center text-center">
              <div
                className="mb-5 p-4 rounded-2xl"
                style={{ background: `linear-gradient(135deg, ${primary}15, ${secondary}15)` }}
              >
                <Sparkles size={32} style={{ color: primary }} />
              </div>

              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                You're invited to chat with
                <br />
                <span style={{ color: primary }}>"{genieName}"</span>
              </h1>
              <p className="text-gray-500 text-base mb-6">
                Enter your email to save this conversation to your Help Genie app and access it later.
              </p>

              {!submitted ? (
                <>
                  <form onSubmit={handleSubmit(onSubmit)} className="w-full space-y-3 mb-4">
                    <div className="text-left">
                      <label className="text-sm font-medium text-gray-700 mb-1 block">
                        Your email address
                      </label>
                      <div className="relative">
                        <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <Input
                          type="email"
                          placeholder="you@example.com"
                          className="pl-9"
                          {...register("email", {
                            required: "Email is required",
                            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Enter a valid email" },
                          })}
                        />
                      </div>
                      {errors.email && (
                        <p className="text-xs text-red-500 mt-1">{errors.email.message}</p>
                      )}
                    </div>
                    <Button
                      type="submit"
                      disabled={isPending}
                      className="w-full text-white font-semibold py-2.5"
                      style={{ background: gradient }}
                    >
                      {isPending ? "Saving…" : "Continue to Chat"}
                    </Button>
                  </form>

                  <button
                    onClick={onSkip}
                    className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    Skip and talk directly
                  </button>
                </>
              ) : (
                <div className="flex flex-col items-center gap-3">
                  <CheckCircle size={40} style={{ color: primary }} />
                  <p className="text-gray-700 font-medium">You're all set!</p>
                  <p className="text-gray-500 text-sm">
                    Check your inbox — we've sent you a link to access your account.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default GenieInvitePage;
